import {setLoading, setError} from '@/store/slices/rewardsSlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {validateRewardName} from '@/services/rewards';

/**
 * Validates reward name for duplication
 * @param {Object} params - Validation parameters
 * @param {boolean} params.shouldValidate - Whether to validate
 * @param {number} params.currentStep - Current step number
 * @param {Object} params.formData - Form data object
 * @returns {Promise<{success: boolean, isDuplicateName?: boolean}|null>} Validation result or null if skipped
 */
export const validateRewardNameIfNeeded = async ({
  shouldValidate,
  currentStep,
  formData
}) => {
  if (!shouldValidate || currentStep !== 1 || formData?.rewardId) {
    return null;
  }

  const params = {
    name: formData?.name?.trim()
  };
  const resp = await validateRewardName(params);
  const isNameValid = resp?.success === true && resp?.data === true;

  if (!isNameValid) {
    return {success: false, isDuplicateName: true};
  }

  return null;
};

/**
 * Wraps an async operation with loading state and error handling
 * @param {Object} params - Handler parameters
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.operation - Async operation to execute
 * @param {Function} params.onSuccess - Optional success callback
 * @param {Function} params.onError - Optional error callback (receives error message)
 * @param {string} params.errorMessage - Default error message
 * @returns {Promise<void>}
 */
export const withLoadingAndErrorHandling = async ({
  dispatch,
  operation,
  onSuccess,
  onError,
  errorMessage = 'Operation failed'
}) => {
  try {
    dispatch(setLoading(true));
    await operation();
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    const errorMsg = error?.message;
    const displayMessage = errorMsg || errorMessage;
    dispatch(setError(errorMsg));
    if (onError) {
      onError(displayMessage);
    } else {
      dispatch(
        setToastNotifications({
          show: true,
          message: displayMessage,
          type: 'error'
        })
      );
    }
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Shows a toast notification
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} message - Toast message
 * @param {string} type - Toast type ('success' | 'error' | 'warning' | 'info')
 */
export const showToast = (dispatch, message, type = 'success') => {
  dispatch(
    setToastNotifications({
      show: true,
      message,
      type
    })
  );
};
